import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useTranslation } from 'react-i18next';
import {
  readStoredDisplayTimezone,
  persistDisplayTimezone,
  syncFormatDefaults,
  type AdminDisplayTimezone,
} from '../utils/displayTimezone';

type DisplayTimezoneContextValue = {
  timezone: AdminDisplayTimezone;
  setTimezone: (tz: AdminDisplayTimezone) => void;
};

const DisplayTimezoneContext = createContext<DisplayTimezoneContextValue | null>(null);

export function DisplayTimezoneProvider({ children }: { children: ReactNode }) {
  const { i18n } = useTranslation();
  const [timezone, setTimezoneState] = useState<AdminDisplayTimezone>(() =>
    typeof window !== 'undefined' ? readStoredDisplayTimezone() : 'local'
  );

  useEffect(() => {
    syncFormatDefaults({ language: i18n.language, timezone });
  }, [i18n.language, timezone]);

  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === 'ubus-admin-timezone') {
        setTimezoneState(readStoredDisplayTimezone());
      }
    };
    const onCustom = () => setTimezoneState(readStoredDisplayTimezone());
    window.addEventListener('storage', onStorage);
    window.addEventListener('ubus-admin-timezone', onCustom);
    return () => {
      window.removeEventListener('storage', onStorage);
      window.removeEventListener('ubus-admin-timezone', onCustom);
    };
  }, []);

  const setTimezone = useCallback((tz: AdminDisplayTimezone) => {
    persistDisplayTimezone(tz);
    setTimezoneState(tz);
  }, []);

  const value = useMemo(() => ({ timezone, setTimezone }), [timezone, setTimezone]);

  return (
    <DisplayTimezoneContext.Provider value={value}>{children}</DisplayTimezoneContext.Provider>
  );
}

export function useDisplayTimezone(): DisplayTimezoneContextValue {
  const ctx = useContext(DisplayTimezoneContext);
  if (!ctx) {
    throw new Error('useDisplayTimezone must be used within DisplayTimezoneProvider');
  }
  return ctx;
}
